import { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData } from '../../services/registrationTypesListServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';

const Registrationtypeslist = () => {
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState({ id: '', type_name: '', reg_type_order: '' });
  const [editingId, setEditingId] = useState(null);
  const [modalType, setModalType] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch data list
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getData();
      setDataList(data);
    }
    catch (error) {
      console.error('Error fetching data:', error);
    }
    finally {
      setTimeout(() => {
        setLoading(false);
      }, 500);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const validateForm = () => {
    const newErrors = {};
    if (!form.reg_type_order) newErrors.reg_type_order = "Order is required";
    if (!form.type_name) newErrors.type_name = "Package Name is required";
    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };

  const handleChange = (e) => {
    try {
      const { name, value } = e.target;
      let updatedErrors = { ...errors };
      setForm((prev) => ({ ...prev, [name]: value }));
      if (value) {
        updatedErrors[name] = ''; // Clear error if corrected
      }
      const isValid = /^[0-9]*$/.test(value);
      if (name === "reg_type_order") { 
        if (value.trim() === '') {
          updatedErrors[name] = "Order is required";
        } 
        else if (!isValid) {
          updatedErrors[name] = "Order must be a number";
        } else if (Number(value) === 0) {
          updatedErrors[name] = "Order value must be greater than zero";
        }
        else{
          const isDuplicate = dataList.some((item) => {
          if (item.id === editingId) return false; // skip comparing with self
          return  item?.reg_type_order === Number(value);
        });
         if (isDuplicate) {
          updatedErrors[name] = "Order is already exists";
        }
        }
      }
      setForm((prev) => ({ ...prev, [name]: value }));
      setErrors(updatedErrors);
    }
    catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      if (!validateForm()) {
        return
      }
      let result;
      if (modalType === 'add') {
        result = await addData(form);
        if (result.status === 200 | 201) {
          fetchData();  // Re-fetch data after adding/editing
          toast.success("Saved successfully!", { position: 'top-right', });
        }
        else {
          toast.error(result?.message || "Failed to update data. Please try again.", { position: 'top-right' });
        }

      } else if (modalType === 'edit') {
        result = await updateData(form);
        if (result.status === 200 | 201) {
          fetchData();  // Re-fetch data after adding/editing
          toast.success("Updated successfully!", { position: 'top-right', });
        }
        else {
          toast.error(result?.message || "Failed to update data. Please try again.", { position: 'top-right' });
        }
      }
      fetchData();  // Re-fetch data after adding/editing
      setShowModal(false);
    }
    catch (error) {
      console.error('Error updating data:', error);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedData) {
        // Optimistically remove the data from the list
        setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
        // Proceed with the actual delete request
        await deleteData(selectedData);
        setShowDeleteConfirmModal(false);
        toast.success("Deleted Successfully!", { position: 'top-right', });
      }
    }
    catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  const openModal = (type, data = { id: '', type_name: '' }) => {
    setModalType(type);
    setEditingId(data?.id);    
    setForm(data);
    setShowModal(true);
  };

  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };


  // Memoized filtered data based on search query
  const filteredData = useMemo(() => {
    const filtered = dataList.filter(
      (data) =>
        data.type_name.toLowerCase().includes(searchText.toLowerCase())
    );
    const sortedData = [...filtered].sort((a, b) => a.reg_type_order - b.reg_type_order);
    return sortedData;
  }, [dataList, searchText]);

  const columns = [
    // {
    //   name: '#',
    //   selector: (row, index) => (currentPage - 1) * 10 + (index + 1),
    //   sortable: false,
    //   width: '80px',
    // },
    {
      name: 'Order',
      selector: (row) => Number(row?.reg_type_order),
      cell: (row) => (
        <span title={row?.reg_type_order}>
          {row?.reg_type_order}
        </span>
      ),
      sortable: true,
    },
    {
      name: 'Package Name',
      selector: (row) => row.type_name,
      cell: (row) => (
        <span title={row.type_name}>
          {row.type_name}
        </span>
      ),
      sortable: true,
    },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openModal('edit', row)} > Edit </Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)} > Delete </Button>
        </div>
      ),
    },
    // {
    //   name: 'Order',
    //   cell: (row) => (
    //     <select className="form-select form-select-sm" defaultValue={row.order} onChange={(e) => handleOrderChange(e, row)}>          
    //       {Array.from({ length: dataList.length }, (_, i) => (
    //         <option key={i + 1} value={i + 1}>{i + 1}</option>
    //       ))}
    //     </select>
    //   ),
    // },
  ];

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8">
                  <p className='mb-3 fs-4'>Basic Setup & File upload / Registration Setup / Registraion Types</p>
                  {/* <h2 className='mb-3'>Registraion Setup / Registraion Types</h2> */}
                </div>
                <div className="col-4 text-end">
                  <button className="btn btn-primary mb-3" onClick={() => openModal('add')}>
                    Add
                  </button>
                </div>
              </div>

              {/* Data Table for data */}
              <DataTable
                columns={columns}
                data={filteredData}
                pagination
                search
                highlightOnHover
                responsive
                striped
                paginationPerPage={10}
                paginationComponentOptions={{
                noRowsPerPage: true,
                }}
                onChangePage={handlePageChange}
                subHeader
                subHeaderComponent={
                  <div>
                    <input className="form-control" type="text" placeholder="Search" value={searchText}
                      onChange={handleSearchChange}
                    />
                  </div>
                }
              />

              {/* Modal for Add/Edit data */}
              <Modal show={showModal} onHide={() => setShowModal(false)} backdrop="static">
                <Modal.Header closeButton>
                  <Modal.Title className='fs-3'>{modalType === 'add' ? 'Add' : 'Edit'}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Order <small className='text-danger'> * </small></Form.Label>
                      <Form.Control type="text" name="reg_type_order" value={form.reg_type_order} onChange={handleChange} isInvalid={!!errors.reg_type_order} />
                      <Form.Control.Feedback type="invalid">
                          {errors.reg_type_order}
                      </Form.Control.Feedback>
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Package Name <small className='text-danger'> * </small></Form.Label>
                      <Form.Control type="text" name="type_name" value={form.type_name} onChange={handleChange} isInvalid={!!errors.type_name} />
                      <Form.Control.Feedback type="invalid">{errors.type_name}</Form.Control.Feedback>
                    </Form.Group>

                    {/* <Form.Group className="mb-3">
                    <Form.Label className='form-label-custom'>Order <small className='text-danger'> * </small></Form.Label>
                    <Form.Control className="form-select mb-3" as="select" name="order" value={form.order} onChange={handleChange} isInvalid={!!errors.order} >
                        <option value="">Select Order</option>
                        <option value="1">1</option>
                        <option value="2">2</option>
                        <option value="3">3</option>
                        <option value="4">4</option>
                        <option value="5">5</option>
                        <option value="6">6</option>
                        <option value="7">7</option>
                        <option value="8">8</option>
                        <option value="9">9</option>
                        <option value="10">10</option>
                        <option value="11">11</option>
                        <option value="12">12</option>
                        <option value="13">13</option>
                        <option value="14">14</option>
                        <option value="15">15</option>
                </Form.Control>
                <Form.Control.Feedback type="invalid">
                    {errors.order}
                </Form.Control.Feedback>
                </Form.Group> */}

                    <Modal.Footer className='border-0'>
                      <Button variant="secondary" onClick={() => setShowModal(false)}> Cancel </Button>
                      <Button variant="primary" type='submit'> {modalType === 'add' ? 'Add' : 'Save'} </Button>
                    </Modal.Footer>
                  </Form>
                </Modal.Body>
              </Modal>

              {/* Delete Confirmation Modal */}
              <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
                <Modal.Header closeButton>
                  <Modal.Title>Delete</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>Are you sure you want to delete this row?</p>
                </Modal.Body>
                <Modal.Footer>
                  <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>
                    Cancel
                  </Button>
                  <Button variant="danger" onClick={handleDelete}>
                    Yes, Delete
                  </Button>
                </Modal.Footer>
              </Modal>
            </div>
            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Registrationtypeslist;